import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  User,
  Heart,
  BookOpen,
  Clock,
  MessageCircle,
  ThumbsUp,
  Users,
  Calendar,
} from 'lucide-react';
import { useGlobalSearch, SearchResult, SearchResultType } from '@/hooks/useGlobalSearch';
import { useDebounce } from '@/hooks/useDebounce';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const typeConfig: Record<SearchResultType, { icon: React.ElementType; label: string; color: string }> = {
  post: { icon: FileText, label: 'Discussions', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  user: { icon: User, label: 'Members', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  prayer: { icon: Heart, label: 'Prayer Requests', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  bible_study: { icon: BookOpen, label: 'Bible Studies', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

function getTimeAgo(dateString?: string): string {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
  return date.toLocaleDateString();
}

function getInitials(title: string): string {
  return title
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function SearchResultItem({ result, onSelect }: { result: SearchResult; onSelect: () => void }) {
  const config = typeConfig[result.type];
  const Icon = config.icon;

  return (
    <CommandItem
      value={`${result.type}-${result.id}-${result.title}`}
      onSelect={onSelect}
      className="flex items-start gap-3 p-3 cursor-pointer"
    >
      <Avatar className="h-10 w-10 flex-shrink-0">
        {result.avatar_url ? (
          <AvatarImage src={result.avatar_url} alt={result.title} />
        ) : null}
        <AvatarFallback className="text-xs bg-slate-100 dark:bg-slate-800">
          {getInitials(result.title)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{result.title}</span>
          {result.category && (
            <Badge variant="secondary" className="text-xs">
              {result.category}
            </Badge>
          )}
        </div>

        {result.subtitle && (
          <p className="text-xs text-muted-foreground">{result.subtitle}</p>
        )}

        {result.description && (
          <p className="text-xs text-muted-foreground line-clamp-1">
            {result.description}
          </p>
        )}

        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Icon className="h-3 w-3" />
            <span>{config.label}</span>
          </div>

          {result.created_at && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{getTimeAgo(result.created_at)}</span>
            </div>
          )}

          {result.metadata?.likes !== undefined && (
            <div className="flex items-center gap-1">
              <ThumbsUp className="h-3 w-3" />
              <span>{result.metadata.likes}</span>
            </div>
          )}

          {result.metadata?.comments !== undefined && (
            <div className="flex items-center gap-1">
              <MessageCircle className="h-3 w-3" />
              <span>{result.metadata.comments}</span>
            </div>
          )}

          {result.metadata?.schedule && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{result.metadata.schedule}</span>
            </div>
          )}
        </div>
      </div>
    </CommandItem>
  );
}

export default function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const { query, setQuery, results, isLoading, search, clearResults } = useGlobalSearch();
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery) {
      search(debouncedQuery);
    }
  }, [debouncedQuery, search]);

  useEffect(() => {
    if (!open) {
      clearResults();
    }
  }, [open, clearResults]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      onOpenChange(false);
      clearResults();

      switch (result.type) {
        case 'post':
          navigate('/discussions', { state: { highlightPost: result.id } });
          break;
        case 'user':
          navigate(`/profile/${result.id}`);
          break;
        case 'prayer':
          navigate('/prayer', { state: { highlightPrayer: result.id } });
          break;
        case 'bible_study':
          navigate('/bible-study', { state: { highlightStudy: result.id } });
          break;
      }
    },
    [navigate, onOpenChange, clearResults]
  );

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<SearchResultType, SearchResult[]>);

  const typeOrder: SearchResultType[] = ['post', 'user', 'prayer', 'bible_study'];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search discussions, members, prayers, Bible studies..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <div className="flex items-center justify-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Searching...
            </div>
          </div>
        )}

        {!isLoading && query.length >= 2 && results.length === 0 && (
          <CommandEmpty>
            <div className="flex flex-col items-center gap-2 py-4">
              <p>No results found for "{query}"</p>
              <p className="text-xs text-muted-foreground">
                Try searching for a different term
              </p>
            </div>
          </CommandEmpty>
        )}

        {!isLoading && query.length < 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p>Type at least 2 characters to search</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {typeOrder.map((type) => {
                const config = typeConfig[type];
                const Icon = config.icon;
                return (
                  <Badge key={type} variant="outline" className="gap-1">
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {!isLoading &&
          typeOrder.map((type, index) => {
            const typeResults = groupedResults[type];
            if (!typeResults || typeResults.length === 0) return null;

            const config = typeConfig[type];

            return (
              <div key={type}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={config.label}>
                  {typeResults.map((result) => (
                    <SearchResultItem
                      key={`${result.type}-${result.id}`}
                      result={result}
                      onSelect={() => handleSelect(result)}
                    />
                  ))}
                </CommandGroup>
              </div>
            );
          })}
      </CommandList>

      <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↑↓</kbd>
            to navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Enter</kbd>
            to select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Esc</kbd>
            to close
          </span>
        </div>
      </div>
    </CommandDialog>
  );
}
